<template>
  <ModuleWrapper
    :moduleId="moduleId"
    :image="imageSrcPath('assets/branding/logo.svg')"
    title="Hero Background"
    class="tw-relative"
    @initializeData="initializeData"
  >
    <template #main>
      <div class="tw-mb-4 tw-block tw-text-xs">
        Keep any content in image centered and leave at least 20% margin on all sides.
      </div>
      <div>
        <StudioToggleWithLabel
          id="show-title-toggle"
          label="Show title"
          class="tw-mb-4"
          :checked="is_title_visible"
          @update:checked="toggleTitleVisible"
        />
        <ConditionalColorPicker
          v-if="is_title_visible"
          id="title-color-picker"
          label="Title Color"
          type="color"
          class="tw-mb-4"
          :initialColor="titleColor.style"
          @colorChange="onTitleColorChange"
        />
      </div>

      <div>
        <StudioSelect
          id="desktop-background-type"
          label="Desktop Background Type"
          :options="[
            { value: 'Image', label: 'Image' },
            { value: 'Solid Color', label: 'Solid Color' },
            { value: 'Gradient', label: 'Gradient' }
          ]"
          size="small"
          orientation="horizontal"
          class="tw-mb-4"
          :value="selectedDesktopBackgroundType"
          @change="onSelectDesktopBackgroundType"
        />
        <ImageUploadButton
          v-if="selectedDesktopBackgroundType === 'Image'"
          label="Min of 1024x768, Max of 2400x1800"
          :image="desktopHeroBackground"
          :acceptedTypes="['.png', '.jpg', '.jpeg', '.ico', '.webp']"
          :s3BucketPath="s3BucketPath"
          :imageValidation="backgroundImageValidationDesktop"
          :security="filestackSecurity"
          required
          class="tw-mb-4"
          @changeImageUrl="onDesktopHeroImageUrlChange"
        />
        <ConditionalColorPicker
          v-else-if="selectedDesktopBackgroundType === 'Solid Color'"
          id="desktop-background-color-picker"
          label="Desktop Background Color"
          type="color"
          class="tw-mb-4"
          :initialColor="desktopColor.style"
          @colorChange="onDesktopColorChange"
        />
        <StudioColorPickerInput
          v-else-if="selectedDesktopBackgroundType === 'Gradient'"
          id="desktop-background-gradient-picker"
          label="Desktop Background Gradient"
          type="gradient"
          class="tw-mb-4"
          :initialGradient="desktopGradient"
          @gradientChange="onDesktopGradientChange"
        />
      </div>

      <div>
        <StudioSelect
          id="mobile-background-type"
          label="Mobile Background Type"
          :options="[
            { value: 'Image', label: 'Image' },
            { value: 'Solid Color', label: 'Solid Color' },
            { value: 'Gradient', label: 'Gradient' }
          ]"
          size="small"
          orientation="horizontal"
          class="tw-mb-4"
          :value="selectedMobileBackgroundType"
          @change="onSelectMobileBackgroundType"
        />
        <ImageUploadButton
          v-if="selectedMobileBackgroundType === 'Image'"
          label="Min of 600x800, Max of 1200x1600"
          :image="mobileHeroBackground"
          :acceptedTypes="['.png', '.jpg', '.jpeg', '.webp']"
          :s3BucketPath="s3BucketPath"
          :imageValidation="backgroundImageValidationMobile"
          :security="filestackSecurity"
          required
          class="tw-mb-4"
          @changeImageUrl="onMobileHeroImageUrlChange"
        />
        <ConditionalColorPicker
          v-else-if="selectedMobileBackgroundType === 'Solid Color'"
          id="mobile-background-color-picker"
          label="Mobile Background Color"
          type="color"
          class="tw-mb-4"
          :initialColor="mobileColor.style"
          @colorChange="onMobileColorChange"
        />
        <StudioColorPickerInput
          v-else-if="selectedMobileBackgroundType === 'Gradient'"
          id="desktop-background-gradient-picker"
          label="Mobile Background Gradient"
          type="gradient"
          class="tw-mb-4"
          :initialGradient="mobileGradient"
          @gradientChange="onMobileGradientChange"
        />
      </div>

      <StudioToggleWithLabel
        id="hide-gradient-toggle"
        label="Remove bottom gradient"
        class="tw-mb-4"
        :checked="isGradientHidden"
        @update:checked="toggleHeroGradient"
      />
    </template>
  </ModuleWrapper>
</template>

<script>
import logger from '@goldcast/logger';
import { mapGetters, mapMutations, mapState } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';
import ConditionalColorPicker from '../components/ConditionalColorPicker.vue';
import ImageUploadButton from '../components/ImageUploadButton.vue';
import { color, gradient } from '../data/defaultValues';

import { getFilestackSecurity } from '@/components/core/Filestack/filestackSecurity';
import { GET_RESOURCES_PATH, BACKGROUND_IMG_VALIDATION_DESKTOP, BACKGROUND_IMG_VALIDATION_MOBILE } from '@/constants';
import StudioColorPickerInput from '@/studioComponents/StudioColorPickerInput.vue';
import StudioSelect from '@/studioComponents/StudioSelect.vue';
import StudioToggleWithLabel from '@/studioComponents/StudioToggleWithLabel.vue';
import {
  destructureRgbColor,
  destructureGradientColor,
  isColorString,
  isUrlString,
  isGradientString,
  destructureUrl
} from '@/utils/utils.js';

const cloneDeep = require('lodash.clonedeep');

export default {
  name: 'HeroBackground',
  components: {
    ModuleWrapper,
    ConditionalColorPicker,
    StudioColorPickerInput,
    StudioSelect,
    StudioToggleWithLabel,
    ImageUploadButton
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedDesktopBackgroundType: 'Image',
      selectedMobileBackgroundType: 'Image',
      s3BucketPath: `${GET_RESOURCES_PATH(this.$route.params.eventId)}`,
      desktopColor: color,
      mobileColor: color,
      desktopGradient: cloneDeep(gradient),
      mobileGradient: cloneDeep(gradient),
      is_title_visible: true,
      titleColor: color,
      backgroundImageValidationDesktop: BACKGROUND_IMG_VALIDATION_DESKTOP,
      backgroundImageValidationMobile: BACKGROUND_IMG_VALIDATION_MOBILE,
      isGradientHidden: false,
      filestackSecurity: null
    };
  },
  async mounted() {
    this.initializeData();
    if (this.$store.getters['org/isSignedFilestackUploadsEnabled']) {
      try {
        const { security } = await getFilestackSecurity({
          intent: 'event_asset',
          assetKind: 'image',
          event: this.$route.params.eventId
        });
        this.filestackSecurity = security;
      } catch (e) {
        logger.warn('Failed to fetch Filestack security, upload will fail', e);
      }
    }
  },
  computed: {
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    ...mapState('branding', ['isCurrentModuleInValid', 'changesMadeInThemeObj', 'currentView']),
    isFormValid() {
      if (this.selectedDesktopBackgroundType === 'Image' && !this.desktopHeroBackground) {
        return false;
      }
      if (this.selectedMobileBackgroundType === 'Image' && !this.mobileHeroBackground) {
        return false;
      }

      return true;
    },
    desktopHeroBackground() {
      const moduleKeys = ['splash_page_hero', 'event_background'];
      const { splash_page_hero, event_background } = this.getInitialModuleData(moduleKeys);
      return isUrlString(splash_page_hero) ? destructureUrl(splash_page_hero) : destructureUrl(event_background);
    },
    mobileHeroBackground() {
      const moduleKeys = ['mobile_splash_page_hero', 'mobile_event_background'];
      const { mobile_splash_page_hero, mobile_event_background } = this.getInitialModuleData(moduleKeys);
      return isUrlString(mobile_splash_page_hero)
        ? destructureUrl(mobile_splash_page_hero)
        : destructureUrl(mobile_event_background);
    }
  },
  watch: {
    isFormValid(newInputs) {
      this.setCurrentModuleInvalid(!newInputs);
    }
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInThemeObj: 'setChangesMadeInThemeObj',
      setChangesMadeInEvent: 'setChangesMadeInEvent',
      setCurrentModuleInvalid: 'setCurrentModuleInvalid'
    }),
    initializeData() {
      const moduleKeys = [
        'splash_page_hero',
        'mobile_splash_page_hero',
        'is_title_visible',
        'splash_text_onimage_color',
        'splash_gradient_height'
      ];
      const {
        splash_page_hero: splashPageHero,
        mobile_splash_page_hero: mobileSplashPageHero,
        is_title_visible: is_title_visible,
        splash_text_onimage_color: splashTextOnImageColor,
        splash_gradient_height: splashGradientHeight
      } = this.getInitialModuleData(moduleKeys);

      if (splashGradientHeight == '0' || splashGradientHeight == '0vh') {
        this.isGradientHidden = true;
      } else {
        this.isGradientHidden = false;
      }

      const isDesktopGradient = isGradientString(splashPageHero);
      const isDesktopSolidColor = isColorString(splashPageHero);
      const isMobileGradient = isGradientString(mobileSplashPageHero);
      const isMobileSolidColor = isColorString(mobileSplashPageHero);
      if (isDesktopGradient) {
        this.desktopGradient = destructureGradientColor(splashPageHero);
        this.selectedDesktopBackgroundType = 'Gradient';
        this.desktopColor = color;
      } else if (isDesktopSolidColor) {
        this.desktopColor = destructureRgbColor(splashPageHero);
        this.selectedDesktopBackgroundType = 'Solid Color';
        this.desktopGradient = cloneDeep(gradient);
      } else {
        this.selectedDesktopBackgroundType = 'Image';
        this.desktopColor = color;
        this.desktopGradient = cloneDeep(gradient);
      }
      if (isMobileGradient) {
        this.mobileGradient = destructureGradientColor(mobileSplashPageHero);
        this.selectedMobileBackgroundType = 'Gradient';
        this.mobileColor = color;
      } else if (isMobileSolidColor) {
        this.mobileColor = destructureRgbColor(mobileSplashPageHero);
        this.selectedMobileBackgroundType = 'Solid Color';
        this.mobileGradient = cloneDeep(gradient);
      } else {
        this.selectedMobileBackgroundType = 'Image';
        this.mobileColor = color;
        this.mobileGradient = cloneDeep(gradient);
      }
      this.is_title_visible = is_title_visible || false;
      if (splashTextOnImageColor) {
        this.titleColor = destructureRgbColor(splashTextOnImageColor);
      }
    },
    onSelectDesktopBackgroundType(e) {
      const selectedType = e.target.value;

      if (selectedType === 'Gradient') {
        this.setChangesMadeInThemeObj({
          splash_page_hero: this.desktopGradient.style
        });
      } else if (selectedType === 'Solid Color') {
        this.setChangesMadeInThemeObj({
          splash_page_hero: this.desktopColor.style
        });
      }

      this.selectedDesktopBackgroundType = selectedType;
    },
    onSelectMobileBackgroundType(e) {
      const selectedType = e.target.value;

      if (selectedType === 'Gradient') {
        this.setChangesMadeInThemeObj({
          mobile_splash_page_hero: this.desktopGradient.style
        });
      } else if (selectedType === 'Solid Color') {
        this.setChangesMadeInThemeObj({
          mobile_splash_page_hero: this.desktopColor.style
        });
      }

      this.selectedMobileBackgroundType = selectedType;
    },
    onDesktopColorChange(color) {
      this.desktopColor = color;
      this.setChangesMadeInThemeObj({
        splash_page_hero: color.style
      });
    },
    onMobileColorChange(color) {
      this.mobileColor = color;
      this.setChangesMadeInThemeObj({
        mobile_splash_page_hero: color.style
      });
    },
    onDesktopGradientChange(gradient) {
      this.desktopGradient = gradient;
      this.setChangesMadeInThemeObj({
        splash_page_hero: gradient.style
      });
    },
    onMobileGradientChange(gradient) {
      this.mobileGradient = gradient;
      this.setChangesMadeInThemeObj({
        mobile_splash_page_hero: gradient.style
      });
    },
    onTitleColorChange(color) {
      this.titleColor = color;
      this.setChangesMadeInThemeObj({
        splash_text_onimage_color: color.style
      });
    },
    onDesktopHeroImageUrlChange(url) {
      this.setChangesMadeInThemeObj({
        splash_page_hero: `Url('${url}')`
      });
    },
    onMobileHeroImageUrlChange(url) {
      this.setChangesMadeInThemeObj({
        mobile_splash_page_hero: `Url('${url}')`
      });
    },
    toggleTitleVisible() {
      this.is_title_visible = !this.is_title_visible;
      this.setChangesMadeInEvent({
        is_title_visible: this.is_title_visible
      });
    },
    toggleHeroGradient() {
      this.isGradientHidden = !this.isGradientHidden;
      this.setChangesMadeInThemeObj({
        splash_gradient_height: this.isGradientHidden ? '0vh' : '60vh'
      });
    }
  }
};
</script>
